import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import StyleConstants from './styles.constants';
import DashboardTheme from '../features/statistics/dashboard/dashboard-theme';
import { inkFor, tint } from './admin-surface.styles';

// Page head, section head and row chrome for the admin lists — offices, parkings and facilities —
// following the approved mockups. Shared from `core/` because all three sections render the same
// full-width section (head → toolbar → flush rows or card grid → footer) from three features.

export const pageHead = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: isMobile ? 'stretch' : 'flex-start',
  justifyContent: 'space-between',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 24,
});

export const pageTitle: CSSProperties = {
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  fontSize: 22,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: DashboardTheme.navy900,
  margin: 0,
};

export const pageSub: CSSProperties = {
  margin: '6px 0 0',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  color: DashboardTheme.gray700,
};

// The "not linked to an office" tail of the summary, which is the one thing on this page that
// asks for attention.
export const pageSubWarning: CSSProperties = {
  color: inkFor(StyleConstants.ORANGE_COLOR),
  fontWeight: 600,
};

export const panelHeadLeft: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  minWidth: 0,
};

export const panelTitle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  fontFamily: StyleConstants.HEADER_FONT_FAMILY,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  color: StyleConstants.DARK_BLUE_COLOR,
  margin: 0,
};

export const countPill: CSSProperties = {
  background: DashboardTheme.gray100,
  borderRadius: 10,
  padding: '1px 8px',
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 11,
  fontWeight: 700,
  color: DashboardTheme.gray700,
};

export const filterSelect = (isMobile: boolean): NestedCSSProperties => ({
  padding: '9px 12px',
  border: `1px solid ${DashboardTheme.gray300}`,
  borderRadius: 10,
  background: StyleConstants.WHITE_COLOR,
  color: DashboardTheme.navy800,
  fontFamily: DashboardTheme.bodyFontFamily,
  fontSize: 13.5,
  cursor: 'pointer',
  maxWidth: isMobile ? undefined : 190,
  width: isMobile ? '100%' : undefined,
  $nest: {
    '&:focus': {
      outline: `2px solid ${StyleConstants.MEDIUM_BLUE_COLOR}`,
      outlineOffset: -1,
      borderColor: StyleConstants.MEDIUM_BLUE_COLOR,
    },
  },
});

export const rowTile = (accent: string): CSSProperties => ({
  width: 38,
  height: 38,
  borderRadius: 10,
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  background: tint(accent, 0.12),
  color: inkFor(accent),
});

export const rowWho: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

export const rowName: CSSProperties = {
  display: 'block',
  fontSize: 13.5,
  fontWeight: 600,
  color: StyleConstants.DARK_BLUE_COLOR,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const rowSub: CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: DashboardTheme.gray500,
};

export const rowGo: CSSProperties = {
  flex: '0 0 auto',
  color: DashboardTheme.gray300,
  lineHeight: 0,
};

export const chipRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 5,
  marginTop: 7,
};

export type ChipVariant = 'solid' | 'muted' | 'warning';

export const chip = (variant: ChipVariant = 'solid'): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  maxWidth: '100%',
  height: 24,
  padding: '0 9px',
  borderRadius: 12,
  fontSize: 11.5,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  border: variant === 'muted' ? `1px solid ${DashboardTheme.gray200}` : undefined,
  background: {
    solid: DashboardTheme.gray100,
    muted: StyleConstants.TRANSPARENT_COLOR,
    warning: tint(StyleConstants.ORANGE_COLOR, 0.22),
  }[variant],
  color: variant === 'warning' ? inkFor(StyleConstants.ORANGE_COLOR) : DashboardTheme.gray700,
});

export const chipIcon: CSSProperties = {
  flex: '0 0 auto',
  lineHeight: 0,
  display: 'flex',
};

export const pager: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 4,
  paddingTop: 16,
};

export const pagerRange: CSSProperties = {
  fontSize: 12,
  color: DashboardTheme.gray500,
  marginRight: 8,
};

// ---------------------------------------------------------------------------- full-width section
// A panel that fills the page: head, toolbar, flush rows (or a card grid) and a footer, each
// separated by hairlines instead of card padding.

export const sectionTile: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  flex: '0 0 auto',
  display: 'grid',
  placeItems: 'center',
  background: tint(StyleConstants.DARK_BLUE_COLOR, 0.1),
  color: StyleConstants.DARK_BLUE_COLOR,
};

// Sits under the section title: how many items nobody can reach yet.
export const warningLine: CSSProperties = {
  display: 'block',
  marginTop: 2,
  fontSize: 12,
  fontWeight: 600,
  color: inkFor(StyleConstants.ORANGE_COLOR),
};

export const toolbar = (isMobile: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: isMobile ? 'stretch' : 'center',
  flexDirection: isMobile ? 'column' : 'row',
  gap: 12,
  flexWrap: 'wrap',
  padding: 20,
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
});

// Pushes itself — and everything after it — to the right on desktop, so the toolbar needs no
// filler element.
export const sortSelect = (isMobile: boolean): NestedCSSProperties => ({
  ...filterSelect(isMobile),
  marginLeft: isMobile ? undefined : 'auto',
});

export const loadingPad: CSSProperties = {
  padding: '40px 0',
};

export const viewToggle: CSSProperties = {
  display: 'inline-flex',
  border: `1px solid ${DashboardTheme.gray300}`,
  borderRadius: 10,
  overflow: 'hidden',
  flex: '0 0 auto',
};

export const viewToggleButton = (active: boolean, isFirst: boolean): CSSProperties => ({
  border: 0,
  borderLeft: isFirst ? 0 : `1px solid ${DashboardTheme.gray300}`,
  background: active ? tint(StyleConstants.DARK_BLUE_COLOR, 0.1) : StyleConstants.WHITE_COLOR,
  color: active ? StyleConstants.DARK_BLUE_COLOR : DashboardTheme.gray500,
  padding: '8px 10px',
  lineHeight: 0,
  cursor: 'pointer',
});

export const flushRow: NestedCSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '12px 16px',
  borderBottom: `1px solid ${DashboardTheme.gray200}`,
  cursor: 'pointer',
  transitionDuration: '120ms',
  transitionProperty: 'background-color',
  $nest: {
    '&:hover': {
      background: tint(StyleConstants.DARK_BLUE_COLOR, 0.04),
    },
    '&:last-child': {
      borderBottom: 0,
    },
  },
};

// The office chips ride along the right of a list row, and drop away when there is no room.
export const rowLinks = (isMobile: boolean): CSSProperties => ({
  display: isMobile ? 'none' : 'flex',
  gap: 6,
  flex: '0 0 auto',
  maxWidth: '45%',
  overflow: 'hidden',
});

export const cardGrid = (isMobile: boolean): CSSProperties => ({
  display: 'grid',
  gap: 16,
  padding: 20,
  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))',
});

export const gridCard: NestedCSSProperties = {
  border: `1px solid ${DashboardTheme.gray200}`,
  borderRadius: 14,
  padding: 16,
  cursor: 'pointer',
  transitionDuration: '150ms',
  transitionProperty: 'border-color, box-shadow',
  $nest: {
    '&:hover': {
      borderColor: StyleConstants.DARK_BLUE_COLOR,
      boxShadow: '0 6px 18px rgba(16, 24, 40, .07)',
    },
  },
};

export const gridCardTop: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
};

export const sectionFoot: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 8,
  padding: '12px 20px',
  borderTop: `1px solid ${DashboardTheme.gray200}`,
};

// Chips sitting beside other content rather than under it: no top spacing, no wrapping.
export const inlineChipRow: CSSProperties = {
  display: 'flex',
  gap: 6,
  minWidth: 0,
  overflow: 'hidden',
};
